import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/useAppDispatch';
import Radio from '@atoms/Radio/Radio';
import { setSaveCard, setSelectedCard } from '@slices/checkoutSlice';
import ScreenReaderMessage from '@atoms/ScreenReaderMessage/ScreenReaderMessage';
import Button from '@atoms/Button/Button';
import KEYS from '@helpers/keys';
import Icon from '@atoms/Icon/Icon';
import Checkbox from '@atoms/Checkbox/Checkbox';
import IconTooltip from '@molecules/IconTooltip/IconTooltip';
import useCustomer from '@hooks/useCustomer';
import { useTranslations } from 'next-intl';
import {
  StyledCardNumber,
  StyledCheckboxWrapper,
  StyledPaymentModeOption,
  StyledPaymentModesBody,
} from '@molecules/CheckoutPaymentOption/CheckoutPaymentOption.styles';
import trackCardPaymentOption, { CardPaymentOption } from '@helpers/analyticsHelpers/trackCardPaymentOption';
import { CreditCardPaymentInfoData } from '@occ/api-client';
import { selectCheckoutPayment } from '@slices/checkoutSlice';
import Delete from '@icons/delete.svg';
import Info from '@icons/info.svg';
import Text from '@atoms/Text/Text';

interface Props {
  savedCards: CreditCardPaymentInfoData[];
  disabled: boolean;
  setDeleteCardId: (cardId: string) => void;
}

const PayexOption = ({ savedCards, disabled, setDeleteCardId }: Props) => {
  const payment = useAppSelector(selectCheckoutPayment);
  const t = useTranslations('paymentModes');
  const tCommon = useTranslations('common');
  const { customer } = useCustomer();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (payment.saveCard) trackCardPaymentOption(CardPaymentOption.SAVE);
  }, [payment.saveCard]);

  useEffect(() => {
    if (payment.selectedCard?.id === 'new') {
      trackCardPaymentOption(CardPaymentOption.NEW);
    } else if (payment.selectedCard?.id) {
      trackCardPaymentOption(CardPaymentOption.EXISTING);
    }
  }, [payment.selectedCard]);

  return (
    <StyledPaymentModesBody $noCards={savedCards.length === 0}>
      {savedCards.map((card) => (
        <StyledPaymentModeOption data-testid="payment-mode-option" key={card.id}>
          <Radio
            checked={payment.selectedCard.id === card.id}
            onChange={() => dispatch(setSelectedCard(card))}
            changeOnKeyEnter
            disabled={disabled}
            id={card.id as string}
          >
            <span data-hj-suppress="true">
              {card.id !== 'new' && <ScreenReaderMessage>{t('card.maskedNumber')}</ScreenReaderMessage>}
              <StyledCardNumber type="body">{card.maskedNumber}</StyledCardNumber>
              {card.expireDate && (
                <>
                  <ScreenReaderMessage>{t('card.expireDate')}</ScreenReaderMessage>
                  <Text type="body" size="small" color="darkerGray">
                    {card.expireDate}
                  </Text>
                </>
              )}
            </span>
          </Radio>
          {card.id !== 'new' && (
            <Button
              type="button"
              tabIndex={0}
              variant="transparent"
              onClick={() => {
                setDeleteCardId(card.id as string);
              }}
              onKeyDown={(e) => {
                if (e.key === KEYS.ENTER) setDeleteCardId(card.id as string);
              }}
              disabled={disabled}
            >
              <Icon title={t('card.delete')} aria-label={t('card.delete')} svg={Delete} size={20} />
            </Button>
          )}
        </StyledPaymentModeOption>
      ))}
      {(!savedCards.length || payment.selectedCard.id === 'new') && !customer?.isAnonymous && (
        <StyledCheckboxWrapper>
          <Checkbox
            checked={payment.saveCard}
            onChange={() => dispatch(setSaveCard(!payment.saveCard))}
            id="saveCardCheckbox"
          >
            {t('card.save')}
          </Checkbox>
          <IconTooltip
            iconProps={{ svg: Info, size: 16, 'aria-label': tCommon('aria.infoIcon') }}
            content={t('tooltip.saveCard')}
          />
        </StyledCheckboxWrapper>
      )}
    </StyledPaymentModesBody>
  );
};

export default PayexOption;
